import { useNavigate } from 'react-router-dom';

import SimpleModal from 'lib/modal/simple-modal';
import {
  SimpleModalContentStyled,
  SimpleModalVerticalActionsStyled,
} from 'lib/modal/simple-modal.styles';
import { PrimaryButton } from 'approot/shared/styles/buttons';
import { spacing16 } from 'approot/shared/styles/spacing.style';
import { Heading18 } from 'approot/shared/styles/typography.style';
import {
  NotificationTip,
  NotificationTipText,
} from 'approot/shared/notifications/notification-tip.style';
import { useSnackbar } from 'approot/shared/snackbar-notification/snackbar-notification.hooks';
import { formatTime } from 'approot/shared/logic/dates';
import { formatDateOrToday } from 'approot/activity-builder/history/history-drawer.utils';
import { useLoggingMutation } from 'lib/apollo/apollo-hooks';
import type {
  RestoreDraftContentMutation,
  RestoreDraftContentMutationVariables,
} from './activity-builder-draft.graphql.generated';
import { RESTORE_DRAFT_CONTENT } from './activity-builder-draft.graphql';
import { getEditUrl } from 'approot/activity-builder/activity-builder-url.utils';
import { useGetInteractiveLessonParams } from 'approot/activity-builder/activity-builder.hooks';
import { LESSON_ACTIVITIES } from 'global/constants';

type Props = {
  contentDraftId: string;
  date: Date;
  closeModal: () => void;
  sectionId?: string;
  isSignedIn: boolean;
};

const ActivityBuilderDraftModal = ({
  contentDraftId,
  closeModal,
  date,
  sectionId,
  isSignedIn,
}: Props) => {
  const navigate = useNavigate();
  const { slug, userClassId, contentType } = useGetInteractiveLessonParams();

  const { showError, showSuccess } = useSnackbar();

  const updatedAtDate = formatDateOrToday(new Date(date));

  const [restoreDraftContent, { loading }] = useLoggingMutation<
    RestoreDraftContentMutation,
    RestoreDraftContentMutationVariables
  >(
    RESTORE_DRAFT_CONTENT,
    {
      variables: {
        input: {
          contentDraftId,
          contentType,
        },
      },
      onCompleted: () => {
        showSuccess('Draft successfully restored');
        navigate(getEditUrl({ slug, userClassId, sectionId, contentType }), {
          replace: true,
        });
      },
      onError: () => {
        showError('Draft unable to be restored');
      },
    },
    isSignedIn
  );

  return (
    <SimpleModal
      isVisible
      title="Restore Draft"
      headerTitle="Restore Draft"
      onClose={closeModal}
    >
      <SimpleModalContentStyled>
        <Heading18 as="h2" marginBottom={spacing16}>
          This action will restore the{' '}
          {contentType === LESSON_ACTIVITIES ? 'lesson' : 'assessment'} back to
          the draft from {updatedAtDate} at {formatTime(date)}.
        </Heading18>
        <NotificationTip $marginBottom={0}>
          <NotificationTipText marginBottom={0}>
            This draft may not look identical when restored. Some components or
            features may no longer be supported or may have been replaced.
          </NotificationTipText>
        </NotificationTip>
        <SimpleModalVerticalActionsStyled>
          <PrimaryButton
            type="button"
            loading={loading}
            disabled={loading}
            onClick={() => restoreDraftContent()}
            data-selector="modal-btn-restore-draft"
          >
            Restore Draft
          </PrimaryButton>
        </SimpleModalVerticalActionsStyled>
      </SimpleModalContentStyled>
    </SimpleModal>
  );
};

export default ActivityBuilderDraftModal;
